import React from "react";
import { motion } from "framer-motion";
import { fadeUp, Section, Kicker } from "@/components/offer/parts";
import { TRAVEL_ZONES, zoneLabel } from "@/lib/travel";

const COPY = {
    en: {
        kicker: "Travel & Location Policy",
        t: "Travel & Location Policy",
        intro: "Travel charges are calculated according to the confirmed filming location and production requirements.",
        colZone: "Filming location", colFee: "Travel charge",
        fee: (z) => (z.mode === "fixed" ? (z.fee_rm === 0 ? "Complimentary" : `RM${z.fee_rm}`) : "Custom quotation"),
        interstate: ["Interstate production", "Travel, transportation, accommodation and other necessary expenses will be quoted separately"],
        pkgTitle: "Package conditions",
        socialIntro: "The following Social Content Packages do not include transportation outside Port Klang or Klang:",
        campaignIntro: "Brand Campaign Packages include basic travel only under these conditions:",
        pkgFooter: "Applicable travel charges must be added during booking or confirmed before the production date.",
        addTitle: "Additional travel costs",
        addIntro: "The following expenses are not included in any package and will be charged separately:",
        addItems: ["Toll charges", "Parking charges", "Valet parking", "Transportation between multiple locations", "Accommodation", "Flight, train or bus tickets", "Equipment transportation or courier charges", "Location permits and entrance fees"],
        multiTitle: "Multiple locations",
        multiIntro: "A package includes only the number of locations stated in the package description. Additional locations may require:",
        multiItems: ["Additional travel charges", "Additional production hours", "Additional crew or equipment costs", "A revised quotation"],
        changeTitle: "Location changes",
        changeIntro: "Any filming location must be confirmed before the production date. Changes to the filming location may affect:",
        changeItems: ["Travel charges", "Production schedule", "Shooting duration", "Equipment requirements", "Final quotation"],
        changeFooter: "Expenses already incurred due to confirmed bookings, location arrangements or transportation are non-refundable.",
    },
    cn: {
        kicker: "旅程与地点政策",
        t: "旅程与地点政策",
        intro: "旅程费用根据确认的拍摄地点与制作需求计算。",
        colZone: "拍摄地点", colFee: "旅程费用",
        fee: (z) => (z.mode === "fixed" ? (z.fee_rm === 0 ? "免费" : `RM${z.fee_rm}`) : "定制报价"),
        interstate: ["跨州制作", "旅程、交通、住宿及其他必要开支另行报价"],
        pkgTitle: "套餐条件",
        socialIntro: "以下社媒内容套餐不包含巴生港 / 巴生以外的交通:",
        campaignIntro: "品牌宣传套餐仅在以下条件下包含基础旅程:",
        pkgFooter: "适用的旅程费用须在预订时加入,或在拍摄日期前确认。",
        addTitle: "额外旅程费用",
        addIntro: "以下开支不包含在任何套餐内,将另行收取:",
        addItems: ["过路费", "停车费", "代客泊车", "多地点之间的交通", "住宿", "机票、火车票或巴士票", "器材运输或快递费用", "场地许可证及入场费"],
        multiTitle: "多个地点",
        multiIntro: "套餐仅包含套餐说明中列明的地点数量。额外地点可能需要:",
        multiItems: ["额外旅程费用", "额外拍摄时数", "额外人员或器材费用", "修订报价"],
        changeTitle: "地点变更",
        changeIntro: "任何拍摄地点须在拍摄日期前确认。拍摄地点的变更可能影响:",
        changeItems: ["旅程费用", "拍摄排期", "拍摄时长", "器材需求", "最终报价"],
        changeFooter: "因已确认的预订、场地安排或交通而已产生的开支不予退还。",
    },
};

function Bullets({ items, testid }) {
    return (
        <ul className="space-y-1.5 text-sm text-cafe-mocha leading-snug" data-testid={testid}>
            {items.map((it, i) => (
                <li key={i} className="flex items-start gap-2.5"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-cafe-olive flex-shrink-0" />{it}</li>
            ))}
        </ul>
    );
}

function Card({ title, intro, items, footer, testid }) {
    return (
        <motion.div {...fadeUp} className="bg-white/60 border border-cafe-espresso/10 rounded-3xl p-7 md:p-8" data-testid={testid}>
            <h3 className="font-display text-2xl text-cafe-espresso tracking-[-0.02em] mb-3">{title}</h3>
            <p className="text-sm text-cafe-mocha leading-relaxed mb-4">{intro}</p>
            <Bullets items={items} />
            {footer && <p className="text-xs text-cafe-espresso/80 font-medium leading-relaxed mt-4 pt-4 border-t border-cafe-espresso/10">{footer}</p>}
        </motion.div>
    );
}

export default function TravelPolicy({ lang, socialPkgs = [], campaignPkgs = [], zones: zonesIn = TRAVEL_ZONES }) {
    const c = COPY[lang] || COPY.en;
    const zones = zonesIn.filter((z) => z.mode !== "tbc");
    return (
        <Section id="travel-policy">
            <motion.div {...fadeUp} className="mb-10 md:mb-14 max-w-3xl">
                <Kicker>{c.kicker}</Kicker>
                <h2 className="font-display text-3xl md:text-5xl lg:text-6xl text-cafe-espresso leading-[1.1] tracking-[-0.03em] mb-6">{c.t}</h2>
                <p className="font-light text-cafe-mocha text-lg leading-relaxed">{c.intro}</p>
            </motion.div>

            {/* Charges table — desktop table, mobile stacked rows */}
            <motion.div {...fadeUp} className="rounded-3xl border border-cafe-espresso/10 bg-white/70 overflow-hidden mb-8 md:mb-10" data-testid="travel-charges-table">
                <div className="hidden md:grid grid-cols-[1fr_auto] gap-6 px-8 py-4 bg-cafe-espresso text-cafe-cream text-[10px] tracking-[0.24em] uppercase font-bold">
                    <span>{c.colZone}</span><span>{c.colFee}</span>
                </div>
                {zones.map((z) => (
                    <div key={z.id} data-testid={`travel-zone-row-${z.id}`}
                        className="flex flex-col md:grid md:grid-cols-[1fr_auto] gap-1 md:gap-6 px-6 md:px-8 py-4 md:py-5 border-t border-cafe-espresso/10 first:border-t-0 md:first:border-t">
                        <span className="text-cafe-espresso font-medium text-[15px]">{zoneLabel(z, lang)}</span>
                        <span className={`text-base md:text-lg font-semibold tabular-nums ${z.mode === "fixed" ? "text-cafe-olive" : "text-cafe-mocha"}`} data-testid={`travel-zone-fee-${z.id}`}>{c.fee(z)}</span>
                    </div>
                ))}
                <div data-testid="travel-zone-row-interstate" className="flex flex-col md:grid md:grid-cols-[1fr_auto] gap-1 md:gap-6 px-6 md:px-8 py-4 md:py-5 border-t border-cafe-espresso/10">
                    <span className="text-cafe-espresso font-medium text-[15px]">{c.interstate[0]}</span>
                    <span className="text-sm text-cafe-mocha md:text-right md:max-w-xs leading-snug">{c.interstate[1]}</span>
                </div>
            </motion.div>

            {/* Package conditions */}
            <motion.div {...fadeUp} className="bg-cafe-espresso text-cafe-cream rounded-3xl p-7 md:p-10 mb-8 md:mb-10" data-testid="travel-package-conditions">
                <h3 className="font-display text-2xl md:text-3xl tracking-[-0.02em] mb-6">{c.pkgTitle}</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div>
                        <p className="text-sm text-cafe-cream/70 leading-relaxed mb-3">{c.socialIntro}</p>
                        <ul className="space-y-1.5 text-sm">
                            {socialPkgs.map((p) => (
                                <li key={p.id} className="flex items-baseline justify-between gap-4 border-b border-cafe-cream/10 pb-1.5">
                                    <span>{p.name}</span><span className="text-[#D4A574] tabular-nums whitespace-nowrap">{p.price_prefix ? `${lang === "cn" ? "" : "From "}${p.price}${lang === "cn" ? " 起" : ""}` : p.price}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <p className="text-sm text-cafe-cream/70 leading-relaxed mb-3">{c.campaignIntro}</p>
                        <ul className="space-y-2.5 text-sm">
                            {campaignPkgs.map((p) => (
                                <li key={p.id} className="border-b border-cafe-cream/10 pb-2">
                                    <div className="flex items-baseline justify-between gap-4"><span>{p.name}</span><span className="text-[#D4A574] tabular-nums whitespace-nowrap">{p.price_prefix ? `${lang === "cn" ? "" : "From "}${p.price}${lang === "cn" ? " 起" : ""}` : p.price}</span></div>
                                    <div className="text-xs text-cafe-cream/60 mt-0.5">{p.locations}</div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
                <p className="text-sm text-[#D4A574] font-medium mt-6 pt-5 border-t border-cafe-cream/10">{c.pkgFooter}</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                <Card title={c.addTitle} intro={c.addIntro} items={c.addItems} testid="travel-additional-costs" />
                <Card title={c.multiTitle} intro={c.multiIntro} items={c.multiItems} testid="travel-multiple-locations" />
                <Card title={c.changeTitle} intro={c.changeIntro} items={c.changeItems} footer={c.changeFooter} testid="travel-location-changes" />
            </div>
        </Section>
    );
}
